import React from "react";
import cx from "classnames";
import { string, oneOf, node, elementType, oneOfType } from "prop-types";
import styles from "./Text.module.scss";

/**
 * Returns a component that renders paragraphs and headings based on props. By default, it renders a `<p>` tag.
 */
const Text = React.forwardRef((props, refProp) => {
  // className, component, variant, weight, decoration
  const {
    align,
    className,
    children,
    component,
    color,
    decoration,
    weight,
    variant,
    ...restProps
  } = props;

  const variantMap = {
    bodyLarge: "p",
    bodyRegular: "p",
    caption: "span",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    overline: "span",
    subheading: "h5",
  };

  const classes = cx(
    styles.classes,
    {
      [styles.bodyLarge]: variant === "bodyLarge",
      [styles.bodyRegular]: variant === "bodyRegular",
      [styles.caption]: variant === "caption",
      [styles.h1]: variant === "h1",
      [styles.h2]: variant === "h2",
      [styles.h3]: variant === "h3",
      [styles.h4]: variant === "h4",
      [styles.overline]: variant === "overline",
      [styles.subheading]: variant === "subheading",
      [styles.bold]: weight === "bold",
      [styles.medium]: weight === "medium",
      [styles.regular]: weight === "regular",
      [styles.underline]: decoration === "underline",
      [styles.none]: decoration === "none",
      [styles.alignInherit]: align === "inherit",
      [styles.alignLeft]: align === "left",
      [styles.alignCenter]: align === "center",
      [styles.alignRight]: align === "right",
      [styles.alignJustify]: align === "justify",
      [styles.purple]: color === "purple",
      [styles.black]: color === "black",
    },
    className
  );

  // Defaults to `bodyRegular`
  const Element = component || variantMap[variant];

  return (
    <Element ref={refProp} className={classes} {...restProps}>
      {children}
    </Element>
  );
});

export default Text;

Text.propTypes = {
  /** Set the text-align */
  align: oneOf(["inherit", "left", "center", "right", "justify"]),
  /** The text to be displayed */
  children: node.isRequired,
  /** For CSS customisation */
  className: string,
  /** Override the underlying element */
  component: oneOfType([string, elementType]),
  /** Add `text-decoration` to the text */
  decoration: oneOf(["underline", "strikethrough", "none"]),
  /** Choose the variant to be used */
  variant: oneOf([
    "h1",
    "h2",
    "h3",
    "h4",
    "subheading",
    "bodyRegular",
    "bodyLarge",
    "overline",
    "caption",
  ]),
  /** Change the weight of the rendered text */
  weight: oneOf(["regular", "medium", "bold"]),
};

Text.defaultProps = {
  align: "inherit",
  variant: "bodyRegular",
  weight: "regular",
};
